import React, { useState, useEffect } from "react";
import { Modal, Button, Dropdown } from "react-bootstrap";
import TauriImage from "../TauriImage/TauriImage";
import "./OpalMissedCallSessionsModal.css";
import { addRecord, getAll, getNextId, getRecordById, updateRecord } from "../../localDB";
import TauriLoader2 from "../TauriLoader2/TauriLoader2";
import OpalAddRemarksModal from "./OpalAddRemarksModal/OpalAddRemarksModal";
import config from '../../config.json';
import { SyncStatusMap } from "../../visualizations/visualizations";

const OpalMissedCallSessionsModal = ({ show, onHide, endDay }) => {
    const [missedCallSessionItems, setMissedCallSessionItems] = useState([]);
    const [customerItems, setCustomerItems] = useState([]);
    const [reasonItems, setReasonItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [showRemarks, setShowRemarks] = useState(false);
    const [rowSelected, setRowSelected] = useState([]);

    const getToday = () => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        return now;
    }

    const fetchData = async () => {
        setLoading(true);
        try {
            const sessions = await getAll("session");
            const customers = await getAll("customer");
            const reasons = await getAll("MissedCallReasons");

            const today = getToday(); // Make sure this is called before filtering

            const MissedCallSessions = sessions.filter(session => {
                if (!session.SessionDate) return false;

                const sessionDate = new Date(session.SessionDate);
                sessionDate.setHours(0, 0, 0, 0); // normalize session date

                const dateCondition = endDay
                    ? sessionDate.getTime() <= today.getTime()
                    : sessionDate.getTime() < today.getTime();

                return (
                    dateCondition &&
                    session.Status === 0 &&
                    !session.PastDueSessionID &&
                    session.Tag == null
                );
            });

            setMissedCallSessionItems(MissedCallSessions);
            setCustomerItems(customers);
            setReasonItems(reasons);

            // Auto-hide modal if no missed call sessions
            if (MissedCallSessions.length === 0 && show) {
                onHide();
            }

        } catch (error) {
            console.log("error fetching!");
        } finally {
            setLoading(false);
        }
    }

    const handleRemarks = (remarks) => {
        setMissedCallSessionItems(prevItems =>
            prevItems.map(item =>
                item.ID === rowSelected.ID ? { ...item, MissedCallRemarks: remarks } : item
            )
        );
    };

    useEffect(() => {
        fetchData();
    }, [show])

    const handleCellClick = (item) => {
        setShowRemarks(true);
        setRowSelected(item);
    };

    const isFormValid = missedCallSessionItems.every(item => item.MissedCallReasonID);

    const CreateMissedCallSession = async (session) => {
        try {
            const sessionID = await getNextId("session");

            const allItems = await getAll("sessionItems");
            const allNotes = await getAll("sessionNotes");
            const allItemInventories = await getAll("SessionInventory");
            const allInventoryImages = await getAll("InvImages");
            const allItemForRequests = await getAll("SessionForRequest");

            const sessionItems = allItems.filter((item) => item.SessionID === session.ID);
            const sessionNotes = allNotes.filter((item) => item.SessionID === session.ID);
            const sessionItemInventory = allItemInventories.filter((item) => item.SessionID === session.ID);
            const sessionInventoryImages = allInventoryImages.filter((item) => item.SessionID === session.ID);
            const sessionItemForRequests = allItemForRequests.filter((item) => item.SessionID === session.ID);

            const sessionDate = new Date(session.SessionDate);

            const missedCall = {
                ...session,
                ID: sessionID,
                SessionDate: sessionDate.toLocaleDateString('en-CA'),
                Status: 0, //pending
                Tag: 1, //past due/missed 
                SyncStatus: SyncStatusMap.create
            }

            const updateSess = {
                ...session,
                PastDueSessionID: sessionID,
                SyncStatus: SyncStatusMap.update
            }

            session.PastDueSessionID = sessionID;

            await addRecord('session', missedCall);
            await updateRecord('session', updateSess);

            const today = new Date().toISOString();
            // add Session items 
            for (const item of sessionItems) {
                const nextID = await getNextId("sessionItems");
                const newItem = {
                    ...item,
                    ID: nextID,
                    SessionID: sessionID,
                    SyncStatus: SyncStatusMap.create
                };
                await addRecord("sessionItems", newItem);
            }

            // Add Session Notes
            for (const note of sessionNotes) {
                const nextID = await getNextId("sessionNotes");
                const newNote = {
                    ...note,
                    ID: nextID, // unique ID for each
                    CreatedDate: today,
                    SessionID: sessionID,                 // reference to new session
                    SyncStatus: SyncStatusMap.create
                };
                await addRecord("sessionNotes", newNote);
            }

            // Add Session Item Inventory
            for (const inv of sessionItemInventory) {
                const nextID = await getNextId("SessionInventory");
                const newInv = {
                    ...inv,
                    ID: nextID,
                    SessionID: sessionID,
                    SyncStatus: SyncStatusMap.create
                };
                await addRecord("SessionInventory", newInv);
            }

            // Add Session Inventory Images
            for (const img of sessionInventoryImages) {
                const nextID = await getNextId("InvImages");
                const newImg = {
                    ...img,
                    ID: nextID,
                    SessionID: sessionID,
                    SyncStatus: SyncStatusMap.create
                };
                await addRecord("InvImages", newImg);
            }

            // Add Session Item For Requests
            for (const fr of sessionItemForRequests) {
                const nextID = await getNextId("SessionForRequest");
                const newFR = {
                    ...fr,
                    ID: nextID,
                    SessionID: sessionID,
                    SyncStatus: SyncStatusMap.create
                };
                await addRecord("SessionForRequest", newFR);
            }
        } catch (err) {
            console.error('Failed to create missed call reference!');
            return null;
        }
    };

    const handleSubmit = async () => {
        if (!isFormValid) return;

        setIsSubmit(true);
        try {
            for (const item of missedCallSessionItems) {
                await CreateMissedCallSession(item);
            }
            onHide();
        } catch (error) {
            console.error("Failed to save sessions:", error);
        } finally {
            setIsSubmit(false);
        }
    }

    useEffect(() => {
        const updatedItems = missedCallSessionItems.map((session) => {
            if (session.MissedCallReasonID == null && reasonItems.length > 0) {
                return { ...session, MissedCallReasonID: reasonItems[0].ID };
            }
            return session;
        });
        setMissedCallSessionItems(updatedItems);
    }, [reasonItems]); // only once reasonItems is loaded

    return (
        <Modal
            className="opal-missedcall-modal"
            show={show}
            onHide={onHide}
            backdrop={missedCallSessionItems.length > 0 ? "static" : true}  // Prevent closing on outside click
            keyboard={false} // Prevent closing with ESC key
            centered>
            <Modal.Header>
                <div>Missed Calls</div>
                <div className="cycle-date">
                    {getToday().toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric"
                    })}
                </div>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <TauriLoader2 width="2.5rem" height="2.5rem" borderwidth="0.35rem" color="#1D5D9B" />
                ) : (
                    <table className="missedcall-table">
                        <thead className="missedcall-table-header">
                            <tr>
                                <th style={{ width: "15rem" }}>Name</th>
                                <th style={{ width: "20rem" }}>Reason</th>
                                <th>Remarks</th>
                            </tr>
                        </thead>
                        <tbody className="missedcall-table-body">
                            {missedCallSessionItems.map((item) => (
                                <tr key={item?.ID}>
                                    <td style={{ width: "15rem" }}>
                                        {customerItems.find(cust => cust.ID === item?.AccountID)?.Name || "Unknown"}
                                    </td>
                                    <td style={{ width: "20rem", maxWidth: "20rem" }}>
                                        <Dropdown className="w-100">
                                            <Dropdown.Toggle className="form-control text-start">
                                                {
                                                    (() => {
                                                        const selectedReason = reasonItems.find(reason => reason.ID === item.MissedCallReasonID);
                                                        return selectedReason ? `(${selectedReason.Code}) ${selectedReason.Description}` : "Select Reason";
                                                    })()
                                                }
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="w-100">
                                                {reasonItems.map((reason) => (
                                                    <Dropdown.Item
                                                        key={reason.ID}
                                                        onClick={() => {
                                                            const updatedItems = missedCallSessionItems.map((session) =>
                                                                session.ID === item.ID
                                                                    ? { ...session, MissedCallReasonID: reason.ID }
                                                                    : session
                                                            );
                                                            setMissedCallSessionItems(updatedItems);
                                                        }}
                                                    >
                                                        ({reason.Code}) {reason.Description}
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </td>
                                    <td style={{ width: "8rem" }} className="clickable" onClick={() => handleCellClick(item)}>
                                        {item?.MissedCallRemarks ?
                                            item?.MissedCallRemarks :
                                            <TauriImage
                                                localImage={"/Images/tabler--edit.svg"}
                                                className={"tac-icon"}
                                                alt="icon"
                                                color={"#000"}
                                            />
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Modal.Body>
            <Modal.Footer>
                {missedCallSessionItems?.length > 0 &&
                    <span>Please select a reason for each missed call before submitting.</span>
                }
                <Button className="submit-btn" onClick={handleSubmit} disabled={!isFormValid || missedCallSessionItems.length === 0 || isSubmit}>
                    {isSubmit &&
                        <TauriLoader2 width="1.2rem" height="1.2rem" borderwidth="0.3rem" color="#FFF" />
                    }
                    <span>Submit</span>
                </Button>
            </Modal.Footer>

            <OpalAddRemarksModal
                show={showRemarks}
                onHide={() => setShowRemarks(false)}
                display={rowSelected?.MissedCallRemarks}
                onUpdate={handleRemarks}
            />
        </Modal >
    );
};

export default OpalMissedCallSessionsModal;
